import SimplePeer from 'simple-peer/simplepeer.min.js'

class WebRTCService {
  constructor() {
    this.peer = null
    this.connected = false
    this.dataChannel = null
    this.onMessageCallback = null
    this.onConnectedCallback = null
    this.onDisconnectedCallback = null
    this.instanceId = Math.random().toString(36).substr(2, 9)
    this.isInitiator = null
    this._reconnecting = false
    this._reconnectAttempts = 0
    this._maxReconnectAttempts = 5
    this._reconnectTimer = null
    console.log('🔧 WebRTCService instance created:', this.instanceId)
    // Backpressure-aware send queue and chunking
    this._outQueue = []
    this._flushTimer = null
    this._flushIntervalMs = 50
    this._maxBuffered = 256000 // 256KB
    this._latestGameState = null
    this._chunkBuffers = {}
    this._maxFrameSize = 16000
  }

  // Initialize as initiator (player who creates the room)
  initAsInitiator() {
    this.isInitiator = true
    console.log('🔧 [' + this.instanceId + '] Initializing as initiator')
    this.peer = new SimplePeer({
      initiator: true,
      trickle: true,
      channelName: 'halloween-game',
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:global.stun.twilio.com:3478' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' }
        ],
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require'
      },
      offerOptions: {
        offerToReceiveAudio: false,
        offerToReceiveVideo: false
      }
    })

    this.setupPeerEvents()
    return this.peer
  }

  // Initialize as receiver (player who joins the room)
  initAsReceiver() {
    this.isInitiator = false
    console.log('🔧 [' + this.instanceId + '] Initializing as receiver')
    this.peer = new SimplePeer({
      initiator: false,
      trickle: true,
      channelName: 'halloween-game',
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:global.stun.twilio.com:3478' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' }
        ],
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require'
      },
      answerOptions: {
        offerToReceiveAudio: false,
        offerToReceiveVideo: false
      }
    })

    this.setupPeerEvents()
    return this.peer
  }

  // Setup peer event handlers
  setupPeerEvents() {
    if (!this.peer) return

    this.peer.on('signal', (signal) => {
      // Signal data will be sent through Firebase
      if (this.onSignalCallback) {
        this.onSignalCallback(signal)
      }
    })

    this.peer.on('connect', () => {
      console.log('✅ WebRTC connection established!')
      this.connected = true
      this._reconnectAttempts = 0 // Reset reconnect counter on successful connection

      // Wait a bit before testing and starting the game
      setTimeout(() => {
        // Configure low buffer threshold if available to help backpressure
        try {
          if (this.peer && this.peer._channel && typeof this.peer._channel.bufferedAmountLowThreshold === 'number') {
            this.peer._channel.bufferedAmountLowThreshold = 65536
            this.peer._channel.onbufferedamountlow = () => this._flushQueue()
          }
        } catch (e) { }

        // Start keepalive pings to keep NAT bindings open on slow/unreliable links
        try {
          this.startKeepAlive()
        } catch (e) {
          console.error('❌ Keepalive failed to start:', e)
        }

        if (this.onConnectedCallback) {
          this.onConnectedCallback()
        }
        this._startFlusher()
      }, 500)
    })

    this.peer.on('data', (data) => {
      // Touch keepalive on any data received
      this.touchKeepAlive()

      try {
        const raw = data.toString()
        const message = JSON.parse(raw)

        // Handle ping messages
        if (message.type === 'ping') {
          // Send pong back
          try {
            this.peer.send(JSON.stringify({ type: 'pong' }))
          } catch (e) { }
          return
        }

        // Handle pong messages
        if (message.type === 'pong') {
          return
        }

        // Handle chunk frames
        if (message.type === 'chunk') {
          const { id, idx, total, payload } = message
          if (!this._chunkBuffers[id]) this._chunkBuffers[id] = { total, parts: [] }
          this._chunkBuffers[id].parts[idx] = payload
          const buf = this._chunkBuffers[id]
          if (buf.parts.filter(p => typeof p === 'string').length === total) {
            const fullStr = buf.parts.join('')
            delete this._chunkBuffers[id]
            const fullMsg = JSON.parse(fullStr)
            if (this.onMessageCallback) {
              this.onMessageCallback(fullMsg)
            }
          }
          return
        }

        if (this.onMessageCallback) {
          this.onMessageCallback(message)
        } else {
          console.warn('⚠️ [' + this.instanceId + '] No message callback registered!')
        }
      } catch (error) {
        console.error('[' + this.instanceId + '] Error parsing WebRTC message:', error)
      }
    })

    console.log('✅ [' + this.instanceId + '] Event listeners attached to peer')

    this.peer.on('error', (error) => {
      console.error('WebRTC error:', error)
      // Don't disconnect on stream errors, they're often non-fatal
      if (error.code === 'ERR_DATA_CHANNEL' || error.code === 'ERR_CONNECTION_FAILURE') {
        this.connected = false
        if (this.onDisconnectedCallback) {
          this.onDisconnectedCallback()
        }
        this.scheduleReconnect()
      }
    })

    this.peer.on('close', () => {
      console.log('WebRTC connection closed')
      this.connected = false
      this.stopKeepAlive()
      this._stopFlusher()
      if (this.onDisconnectedCallback) {
        this.onDisconnectedCallback()
      }
      this.scheduleReconnect()
    })
  }

  // Process signal from the other peer
  signal(signalData) {
    if (this.peer) {
      this.peer.signal(signalData)
    }
  }

  // Send game state update
  sendGameState(gameState) {
    // Coalesce to latest; flush loop will send when possible
    this._latestGameState = gameState
    this._enqueue({ type: 'coalesced-game-state' })
  }

  // Send player action
  sendAction(action, payload) {
    this._enqueue({ type: 'action', action, payload })
  }

  // Send chat message
  sendMessage(message) {
    this._enqueue({ type: 'message', data: message })
  }

  // Register callback for when signal is generated
  onSignal(callback) {
    this.onSignalCallback = callback
  }

  // Register callback for incoming messages
  onMessage(callback) {
    console.log('🔧 [' + this.instanceId + '] Registering message callback')
    this.onMessageCallback = callback
  }

  // Register callback for connection established
  onConnected(callback) {
    this.onConnectedCallback = callback
  }

  // Register callback for disconnection
  onDisconnected(callback) {
    this.onDisconnectedCallback = callback
  }

  // Close connection
  destroy() {
    if (this.peer) {
      this.peer.destroy()
      this.peer = null
    }
    this.connected = false
    this.stopKeepAlive()
    this._reconnecting = false
    this._reconnectAttempts = 0
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
    this._stopFlusher()
    this._outQueue = []
    this._latestGameState = null
  }

  // Check if connected
  isConnected() {
    return this.connected
  }

  // Keepalive ping/pong for NAT and slow links
  startKeepAlive() {
    if (this._keepAliveTimer) return
    this._lastPongAt = Date.now()
    this._keepAliveTimer = setInterval(() => {
      if (!this.peer || this.peer.destroyed) return
      try {
        this.peer.send(JSON.stringify({ type: 'ping' }))
      } catch (e) {
        console.warn('⚠️ Failed to send keepalive ping:', e)
      }
      // If no pong (any data) observed for 30s on slow connections, consider link flaky
      if (Date.now() - (this._lastPongAt || 0) > 30000) {
        console.warn('⚠️ No keepalive response for 30s, connection may be lost')
        if (this.onDisconnectedCallback) {
          this.onDisconnectedCallback()
        }
      }
    }, 3000) // More frequent pings for slow connections
  }

  touchKeepAlive() {
    this._lastPongAt = Date.now()
  }

  stopKeepAlive() {
    if (this._keepAliveTimer) {
      clearInterval(this._keepAliveTimer)
      this._keepAliveTimer = null
    }
  }

  // Automatic reconnection with exponential backoff
  scheduleReconnect() {
    if (this._reconnecting) return
    if (this._reconnectAttempts >= this._maxReconnectAttempts) return
    this._reconnecting = true
    const delayMs = Math.min(16000, Math.pow(2, this._reconnectAttempts) * 1000) // 1s,2s,4s,8s,16s
    this._reconnectTimer = setTimeout(() => {
      this._reconnecting = false
      this._reconnectAttempts += 1
      this.reconnect()
    }, delayMs)
  }

  reconnect() {
    if (this.peer) {
      try { this.peer.destroy() } catch (e) { }
      this.peer = null
    }
    this.connected = false
    // Recreate peer in the same role and reattach handlers; external signal callbacks remain the same
    if (this.isInitiator === true) {
      this.initAsInitiator()
    } else if (this.isInitiator === false) {
      this.initAsReceiver()
    } else {
      return
    }
  }

  // Internal: queue and flush with backpressure and chunking
  _enqueue(obj) {
    if (obj.type === 'coalesced-game-state') {
      const hasMarker = this._outQueue.some(m => m.type === 'coalesced-game-state')
      if (!hasMarker) this._outQueue.push(obj)
    } else {
      this._outQueue.push(obj)
    }
    this._flushQueue()
    this._startFlusher()
  }

  _startFlusher() {
    if (this._flushTimer) return
    this._flushTimer = setInterval(() => this._flushQueue(), this._flushIntervalMs)
  }

  _stopFlusher() {
    if (this._flushTimer) {
      clearInterval(this._flushTimer)
      this._flushTimer = null
    }
  }

  _flushQueue() {
    if (!this.peer || !this.connected) return
    const ch = this.peer._channel
    if (!ch || ch.readyState !== 'open') return
    if (typeof ch.bufferedAmount === 'number' && ch.bufferedAmount > this._maxBuffered) return

    const idx = this._outQueue.findIndex(m => m.type === 'coalesced-game-state')
    if (idx !== -1) {
      const gs = this._latestGameState
      if (gs) {
        const msg = { type: 'gameState', data: gs }
        this._sendJson(msg)
      }
      this._outQueue.splice(idx, 1)
    }

    let sent = 0
    const maxPerTick = 5
    while (this._outQueue.length > 0 && sent < maxPerTick) {
      const next = this._outQueue.shift()
      if (next.type !== 'coalesced-game-state') {
        this._sendJson(next)
        sent++
      }
      if (typeof ch.bufferedAmount === 'number' && ch.bufferedAmount > this._maxBuffered) break
    }
  }

  _sendJson(obj) {
    try {
      const jsonStr = JSON.stringify(obj)
      if (jsonStr.length <= this._maxFrameSize) {
        this.peer.send(jsonStr)
      } else {
        const id = Math.random().toString(36).slice(2)
        const total = Math.ceil(jsonStr.length / this._maxFrameSize)
        for (let i = 0; i < total; i++) {
          const slice = jsonStr.slice(i * this._maxFrameSize, (i + 1) * this._maxFrameSize)
          this.peer.send(JSON.stringify({ type: 'chunk', id, idx: i, total, payload: slice }))
        }
      }
    } catch (e) {
      this._outQueue.unshift(obj)
    }
  }
}

export default new WebRTCService()
